import type {
  AccountInfo,
  AuthenticationResult,
  PublicClientApplication as PublicClientApplicationType,
} from "@azure/msal-browser";
import { PublicClientApplication } from "@azure/msal-browser";
import type { PostHog } from "posthog-js";
import { config } from "~initializer/config.ts";
import { navigateTo } from "~lib/browserNavigation.ts";

/**
 * Auth payload seeded by the e2e test runner into localStorage before the app
 * boots (via Playwright storageState). See e2e/helpers/auth.ts for the producing
 * side.
 */
export interface E2eAuth {
  accessToken: string;
  account: AccountInfo;
}

/**
 * localStorage key holding the fake auth. Stored (not a JS global) on purpose:
 * it then behaves like real MSAL cache — it survives a normal reload but is
 * cleared by logout and by "reset app data", so those correctly log out.
 */
const E2E_AUTH_KEY = "__E2E_AUTH__";

/**
 * Returns the seeded e2e auth if — and only if — we run in the e2e environment
 * and the test runner seeded it. This double guard (build-time environment +
 * runtime seed) makes sure the fake-auth path can never activate in a real
 * deployment, and lets the same e2e build still exercise the real Azure login
 * when nothing is seeded.
 */
export function getE2eAuth(): E2eAuth | undefined {
  if (config().systemEnvironment !== "e2e") {
    return undefined;
  }
  const raw = window.localStorage.getItem(E2E_AUTH_KEY);
  if (!raw) {
    return undefined;
  }
  try {
    return JSON.parse(raw) as E2eAuth;
  } catch {
    return undefined;
  }
}

/**
 * Builds a fake MSAL instance for e2e tests that skips Azure entirely: it
 * reports the injected account as logged in and hands the pre-minted access
 * token to the api client.
 *
 * It is a Proxy over a real (but never network-touching) PublicClientApplication
 * so that @azure/msal-react's internals keep working, while only the
 * account/token/redirect methods the app relies on are overridden.
 */
export async function createE2eFakeMsalInstance(
  auth: E2eAuth,
  postHogClient: PostHog,
): Promise<PublicClientApplicationType> {
  const real = new PublicClientApplication({
    auth: {
      clientId: config().msalClientId ?? "",
      authority: config().msalAuthority ?? "",
      redirectUri: config().msalRedirectUri ?? "",
      postLogoutRedirectUri: config().msalRedirectUri ?? "",
    },
    cache: { cacheLocation: "localStorage" },
  });
  await real.initialize();

  const { account, accessToken } = auth;
  const result = {
    authority: config().msalAuthority ?? "",
    uniqueId: account.localAccountId,
    tenantId: account.tenantId,
    scopes: [],
    account,
    idToken: "",
    idTokenClaims: account.idTokenClaims ?? {},
    accessToken,
    fromCache: true,
    expiresOn: new Date(Date.now() + 8 * 60 * 60 * 1000),
    tokenType: "Bearer",
    correlationId: "e2e",
  } as unknown as AuthenticationResult;

  postHogClient.identify(account.username, { email: account.username });

  // Only these account/token/redirect methods are faked; everything else
  // (initialize, event callbacks, logger, internal controller) delegates to the
  // real instance so msal-react keeps functioning.
  const overrides: Record<string, unknown> = {
    getActiveAccount: () => account,
    getAllAccounts: () => [account],
    getAccount: () => account,
    setActiveAccount: () => undefined,
    acquireTokenSilent: async () => result,
    acquireTokenPopup: async () => result,
    ssoSilent: async () => result,
    acquireTokenRedirect: async () => undefined,
    loginRedirect: async () => undefined,
    loginPopup: async () => result,
    logoutRedirect: async () => {
      // Emulate a real logout: drop the fake auth and reload, so getE2eAuth()
      // returns nothing and the app renders the login screen.
      window.localStorage.removeItem(E2E_AUTH_KEY);
      navigateTo(config().msalRedirectUri || "/");
    },
    logoutPopup: async () => undefined,
    handleRedirectPromise: async () => null,
  };

  return new Proxy(real, {
    get(target, prop) {
      if (typeof prop === "string" && prop in overrides) {
        return overrides[prop];
      }
      const value = Reflect.get(target, prop, target);
      return typeof value === "function" ? value.bind(target) : value;
    },
  });
}
